import { layoutTokenForCode, normalizeChord, parseChord } from './chord';
import type {
  Chord,
  KeybindingCommandId,
  KeyboardLayoutSnapshot,
  Platform,
} from './keybindingTypes';
import { aliasModifiersOf, usLayoutAlias, usLayoutKeyForCode } from './usLayoutAlias';

// Electron 34.3.0 source of truth:
// https://github.com/electron/electron/blob/v34.3.0/lib/browser/api/menu-item-roles.ts
// `close` is deliberately overridden by appMenuTemplate.ts to Mod+Shift+W.
export type RetainedMenuRole = {
  readonly role: string;
  readonly chords: readonly string[];
};

const COMMON_ROLES: readonly RetainedMenuRole[] = [
  { role: 'close', chords: ['Mod+Shift+W'] },
  { role: 'undo', chords: ['Mod+Z'] },
  { role: 'cut', chords: ['Mod+X'] },
  { role: 'copy', chords: ['Mod+C'] },
  { role: 'paste', chords: ['Mod+V'] },
  { role: 'delete', chords: [] },
  { role: 'selectAll', chords: ['Mod+A'] },
  { role: 'resetZoom', chords: ['Mod+0'] },
  // Plus is Electron's accelerator token; Shift+= is the DOM chord on its US
  // mapping. Keep both reserved so recorder and imported settings agree.
  { role: 'zoomIn', chords: ['Mod++', 'Mod+Shift+='] },
  { role: 'zoomOut', chords: ['Mod+-'] },
  { role: 'minimize', chords: ['Mod+M'] },
  { role: 'help', chords: [] },
];

const DEV_ROLES: readonly RetainedMenuRole[] = [
  { role: 'reload', chords: ['Mod+R'] },
  { role: 'forceReload', chords: ['Mod+Shift+R'] },
  { role: 'toggleDevTools', chords: [] },
];

function platformRoles(targetPlatform: Platform): readonly RetainedMenuRole[] {
  const redo = targetPlatform === 'windows' ? 'Ctrl+Y' : 'Mod+Shift+Z';
  const fullscreen = targetPlatform === 'mac' ? 'Ctrl+Cmd+F' : 'F11';
  const quit = targetPlatform === 'windows' ? [] : ['Mod+Q'];
  const devTools = targetPlatform === 'mac' ? ['Cmd+Alt+I'] : ['Ctrl+Shift+I'];
  const roles: RetainedMenuRole[] = [
    { role: 'redo', chords: [redo] },
    { role: 'togglefullscreen', chords: [fullscreen] },
    { role: 'quit', chords: quit },
    { role: 'about', chords: [] },
  ];
  if (targetPlatform === 'mac') {
    roles.push(
      { role: 'services', chords: [] },
      { role: 'hide', chords: ['Cmd+H'] },
      { role: 'hideOthers', chords: ['Cmd+Alt+H'] },
      { role: 'unhide', chords: [] },
      { role: 'zoom', chords: [] },
      { role: 'front', chords: [] },
    );
  }
  return roles.map((entry) => entry.role === 'toggleDevTools'
    ? { ...entry, chords: devTools }
    : entry);
}

export function retainedRoleAccelerators(targetPlatform: Platform, isDev: boolean): readonly RetainedMenuRole[] {
  const devRoles = isDev
    ? DEV_ROLES.map((entry) => entry.role === 'toggleDevTools'
      ? { ...entry, chords: [targetPlatform === 'mac' ? 'Cmd+Alt+I' : 'Ctrl+Shift+I'] }
      : entry)
    : [];
  return [...COMMON_ROLES, ...platformRoles(targetPlatform), ...devRoles];
}

export function findUncataloguedRetainedRoles(
  templateRoles: readonly string[],
  targetPlatform: Platform,
  isDev: boolean,
): string[] {
  const catalogued = new Set(retainedRoleAccelerators(targetPlatform, isDev).map(({ role }) => role));
  return [...new Set(templateRoles)].filter((role) => !catalogued.has(role));
}

function platformChord(value: string, targetPlatform: Platform): string | null {
  const chord = parseChord(value);
  if (!chord || (targetPlatform !== 'mac' && chord.modifiers.includes('Cmd'))) return null;
  const mapped = chord.modifiers.map((modifier) => modifier === 'Mod'
    ? (targetPlatform === 'mac' ? 'Cmd' : 'Ctrl')
    : modifier);
  return normalizeChord({ ...chord, modifiers: [...new Set(mapped)] as typeof chord.modifiers });
}

export type ReservedChord = {
  readonly chord: string;
  readonly source: 'os' | 'menu-role';
  readonly role?: string;
  readonly layoutDependent?: boolean;
  readonly layoutUnknown?: boolean;
};

export function reservedChords(targetPlatform: Platform, isDev: boolean): readonly ReservedChord[] {
  // Chromium editing behavior treats Ctrl+Insert, Shift+Insert, and Shift+Delete as clipboard chords on Windows and Linux only.
  const osChords = targetPlatform === 'mac'
    ? ['Cmd+Tab']
    : ['Alt+F4', 'Ctrl+Alt+Delete', 'Ctrl+Insert', 'Shift+Insert', 'Shift+Delete'];
  const reserved: ReservedChord[] = osChords.map((chord) => ({ chord, source: 'os' }));
  for (const { role, chords } of retainedRoleAccelerators(targetPlatform, isDev)) {
    for (const chord of chords) reserved.push({ chord, source: 'menu-role', role });
  }
  return reserved;
}

export function findReservedChord(
  value: string,
  targetPlatform: Platform,
  isDev: boolean,
  layoutMap?: KeyboardLayoutSnapshot,
): ReservedChord | null {
  const parsed = parseChord(value);
  // KeyboardEvent.metaKey represents Super/Windows outside macOS. D5 does not
  // expose it as a bindable modifier there because those combinations belong
  // to the OS/window manager as a family, rather than a finite chord list.
  if (targetPlatform !== 'mac' && parsed?.modifiers.includes('Cmd')) {
    return { chord: value, source: 'os' };
  }
  const candidate = platformChord(value, targetPlatform);
  if (!candidate) return null;
  const reserved = reservedChords(targetPlatform, isDev);
  const direct = reserved.find(
    ({ chord }) => platformChord(chord, targetPlatform) === candidate,
  );
  if (direct || !parsed?.physical) return direct ?? null;

  if (layoutMap?.status !== 'ready' && /^(?:Key[A-Z]|Digit[0-9])$/.test(parsed.key)) {
    const unresolvedAlias = findUnresolvedLetterOrDigitAlias(parsed, reserved, targetPlatform);
    return unresolvedAlias
      ? { chord: value, source: unresolvedAlias.source, layoutDependent: true, layoutUnknown: true }
      : null;
  }
  // Ready layout: the key it names, else (no entry, or a non-ASCII one on a
  // non-Latin letter layout) the US-layout stand-in the dispatcher matches too (FR1).
  const named = layoutMap?.status === 'ready' ? layoutTokenForCode(parsed.key, layoutMap) : null;
  const mappedKey = named ?? (layoutMap?.status === 'ready'
    ? usLayoutAlias(parsed.key, aliasModifiersOf(parsed.modifiers), targetPlatform, layoutMap)
    : usLayoutKeyForCode(parsed.key));
  if (!mappedKey) return null;
  const logicalCandidate = platformChord(normalizeChord({
    ...parsed,
    key: mappedKey,
    physical: false,
    displayKey: undefined,
  }) ?? '', targetPlatform);
  if (!logicalCandidate) return null;
  const alias = reserved.find(
    ({ chord }) => platformChord(chord, targetPlatform) === logicalCandidate,
  );
  if (!alias) return null;
  return named
    ? { ...alias, layoutDependent: true }
    : { ...alias, layoutDependent: true, layoutUnknown: true };
}
function modifierSignature(chord: Chord, targetPlatform: Platform): string | null {
  if (targetPlatform !== 'mac' && chord.modifiers.includes('Cmd')) return null;
  const modifiers = chord.modifiers.map((modifier) => modifier === 'Mod'
    ? (targetPlatform === 'mac' ? 'Cmd' : 'Ctrl')
    : modifier);
  return [...new Set(modifiers)].sort().join('+');
}
function findUnresolvedLetterOrDigitAlias(
  candidate: Chord,
  reserved: readonly ReservedChord[],
  targetPlatform: Platform,
): ReservedChord | null {
  const candidateModifiers = modifierSignature(candidate, targetPlatform);
  if (candidateModifiers === null) return null;
  return reserved.find(({ chord }) => {
    const parsedReserved = parseChord(chord);
    if (!parsedReserved || parsedReserved.physical) return false;
    return /^[A-Z0-9]$/.test(parsedReserved.key) &&
      modifierSignature(parsedReserved, targetPlatform) === candidateModifiers;
  }) ?? null;
}
export function isScopedReservedException(
  commandId: KeybindingCommandId,
  value: string,
  targetPlatform: Platform,
  isDev: boolean,
): boolean {
  const candidate = platformChord(value, targetPlatform);
  if (commandId === 'debugger.stepInto') return candidate === platformChord('F11', targetPlatform);
  return isDev && commandId === 'query.refreshIntellisense' &&
    candidate === platformChord('Mod+Shift+R', targetPlatform);
}
